import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Spin, Typography, Space, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import { getControlTowerSummary, refreshControlTowerScores } from '@/services/supplierLifecycle.service';

const { Title, Text } = Typography;

export const ProcurementControlTowerPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Record<string, unknown> | null>(null);

  const load = () => {
    setLoading(true);
    getControlTowerSummary()
      .then((d) => setData(d as Record<string, unknown>))
      .catch(() => message.error('Failed to load control tower'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onRefreshScores = async () => {
    try {
      await refreshControlTowerScores();
      message.success('Supplier scores refreshed');
      load();
    } catch {
      message.error('Score refresh failed');
    }
  };

  if (loading || !data) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  const segmentMix = (data.segment_mix ?? {}) as Record<string, number>;
  const threatening = (data.threatening_suppliers ?? []) as Array<Record<string, unknown>>;
  const concentration = (data.spend_concentration ?? []) as Array<Record<string, unknown>>;
  const pendingBlacklist = (data.pending_blacklist_approvals ?? []) as Array<Record<string, unknown>>;
  const dueReeval = (data.due_reevaluations ?? []) as Array<Record<string, unknown>>;
  const deserveMore = (data.deserve_more_business ?? []) as Array<Record<string, unknown>>;
  const requireAudits = (data.require_audits ?? []) as Array<Record<string, unknown>>;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Procurement Control Tower</Title>
        <Button onClick={load}>Refresh</Button>
        <Button type="primary" onClick={onRefreshScores}>Recalculate all scores</Button>
        <Button onClick={() => navigate('/scm/procurement/spot-purchase')}>Spot / Market Purchase</Button>
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="High-risk suppliers" value={Number(data.high_risk_count ?? 0)} /></Card></Col>
        <Col span={6}><Card><Statistic title="Threatening delivery" value={threatening.length} valueStyle={{ color: threatening.length ? '#cf1322' : undefined }} /></Card></Col>
        <Col span={6}><Card><Statistic title="Audits due" value={requireAudits.length} /></Card></Col>
        <Col span={6}><Card><Statistic title="Pending blacklist MD approval" value={pendingBlacklist.length} /></Card></Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="Supplier segmentation" style={{ marginBottom: 16 }}>
            {Object.entries(segmentMix).map(([seg, count]) => (
              <div key={seg} style={{ marginBottom: 8 }}>
                <Tag>{seg}</Tag> <Text>{count}</Text>
              </div>
            ))}
          </Card>
          <Card title="Deserve more business (Strategic / Preferred)">
            <Table size="small" rowKey="id" dataSource={deserveMore}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name' },
                { title: 'Segment', dataIndex: 'supplier_segment', render: (v: string) => <Tag>{v}</Tag> },
                { title: 'Score', dataIndex: 'enterprise_supplier_score' },
              ]}
              pagination={false}
              onRow={(r) => ({ onClick: () => navigate(`/scm/procurement/suppliers/${r.id}`), style: { cursor: 'pointer' } })}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Threaten delivery (overdue POs)" style={{ marginBottom: 16 }}>
            <Table size="small" rowKey="supplier_id" dataSource={threatening}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name' },
                { title: 'Overdue POs', dataIndex: 'overdue_count' },
                { title: 'Open value', dataIndex: 'open_value', render: (v: number) => formatCurrency(Number(v ?? 0)) },
              ]}
              pagination={false}
            />
          </Card>
          <Card title="Spend concentration (12m)">
            <Table size="small" rowKey="supplier_id" dataSource={concentration}
              columns={[
                {
                  title: 'Supplier',
                  key: 'supplier',
                  render: (_: unknown, r: Record<string, unknown>) => (
                    <div>
                      <Text strong>{String(r.supplier_name ?? '—')}</Text>
                      {r.supplier_code ? (
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{String(r.supplier_code)}</Text></div>
                      ) : null}
                    </div>
                  ),
                },
                { title: 'Spend', dataIndex: 'total_spend', render: (v: number) => formatCurrency(Number(v ?? 0)) },
                { title: 'Share %', dataIndex: 'share_pct' },
              ]}
              pagination={false}
              onRow={(r) => ({
                onClick: () => r.supplier_id && navigate(`/scm/procurement/suppliers/${r.supplier_id}`),
                style: { cursor: r.supplier_id ? 'pointer' : 'default' },
              })}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="Due for re-evaluation">
            <Table size="small" rowKey="id" dataSource={dueReeval}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name' },
                { title: 'Scheduled', dataIndex: 'scheduled_date' },
              ]}
              pagination={false}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Pending blacklist approvals">
            <Table size="small" rowKey="id" dataSource={pendingBlacklist}
              columns={[
                { title: 'Supplier', dataIndex: 'supplier_name' },
                { title: 'Reason', dataIndex: 'blacklist_reason', ellipsis: true },
                { title: 'Severity', dataIndex: 'severity' },
              ]}
              pagination={false}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProcurementControlTowerPage;
